import { Box } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateImpregnationCache, useCreateImpregnation, useUpdatetImpregnation } from 'src/api/impregnation'
import BasicDatePicker from 'src/components/DateTimePicker'
import { AppForm, FormSelectField, FormTextField } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { ImpregnationType } from 'src/enums'
import { IImpregnation } from 'src/generated-types/impregnation'
import { useZodForm } from 'src/hooks/zod-form'
import { optionalNumberField } from 'src/screens/localities/positions/schema'
import { ShowDialogProps } from 'src/show-dialog'
import { getUTCDate } from 'src/utils/function'
import { z } from 'zod'

interface ImpregnationFormDialogProps extends ShowDialogProps {
  mainComponentId: EntityId
  initialData?: IImpregnation
  serviceStationId?: EntityId
  pop
}

const ImpregnationFormDialog = ({
  mainComponentId,
  serviceStationId,
  initialData,
  pop,
}: ImpregnationFormDialogProps) => {
  const { t } = useTranslation(['impregnation', 'common'])
  const { mutateAsync: addImpregnation, isLoading: isCreating } = useCreateImpregnation(mainComponentId)
  const { mutateAsync: updateImpregnation, isLoading: isUpdating } = useUpdatetImpregnation(
    mainComponentId,
    initialData?.id as EntityId,
  )
  const schema = z.object({
    impregnationType: z
      .string({
        invalid_type_error: t('provideValidInput', { ns: 'common' }),
      })
      .transform(s => parseInt(s))
      .or(z.number().min(0, { message: t('provideValidInput', { ns: 'common' }) })),
    impregnationDate: z
      .date()
      .or(z.string())
      .default(() => getUTCDate(new Date()) || new Date())
      .transform(d => getUTCDate(new Date(d)) || new Date()),
    evaluation: optionalNumberField.refine(val => Number(val) >= 0 && Number(val) <= 100, {
      message: t('evaluation-should-be-in-range', { ns: 'impregnation' }),
    }),
    consumption: z
      .union([z.number(), z.string().refine(s => s !== '', t('provideValidInput', { ns: 'common' }))])
      .default(0)
      .refine(val => Number(val) >= 0, {
        message: t('consumption-cannot-be-negative', { ns: 'impregnation' }),
      }),
    comment: z.string().optional().nullable(),
  })

  const isEdit = !!initialData?.id

  const form = useZodForm(schema, { defaultValues: initialData })
  const [date, setDate] = useState(
    initialData?.impregnationDate ? getUTCDate(new Date(initialData.impregnationDate)) : getUTCDate(new Date()),
  )

  const onSubmit = async (formData: any) => {
    if (!isEdit)
      await addImpregnation(
        { ...formData, mainComponentId: mainComponentId as number, serviceStationId: serviceStationId as number },
        {
          onSuccess: () => {
            toast.success(t('impregnation-added-successfully', { ns: 'impregnation' }))
            invalidateImpregnationCache.getImpregnations(mainComponentId)
            invalidationActivityLogCache.getMainComponentActivityLogs({
              mainComponentId: mainComponentId as EntityId,
            })
          },
          onError: () => {
            toast.error(t('error-while-adding-impregnation', { ns: 'impregnation' }))
          },
        },
      )
    else
      await updateImpregnation(
        {
          ...formData,
          mainComponentId: mainComponentId as number,
          serviceStationId: serviceStationId as number,
          id: initialData?.id as number,
        },
        {
          onSuccess: () => {
            toast.success(t('impregnation-updated-successfully', { ns: 'impregnation' }))
            invalidateImpregnationCache.getImpregnations(mainComponentId)
            invalidationActivityLogCache.getMainComponentActivityLogs({
              mainComponentId: mainComponentId as EntityId,
            })
          },
          onError: () => {
            toast.error(t('error-while-updating-impregnation', { ns: 'impregnation' }))
          },
        },
      )
    pop()
  }

  return (
    <GenericDialogLayout
      title={isEdit ? t('edit-impregnation', { ns: 'impregnation' }) : t('add-impregnation', { ns: 'impregnation' })}
      pop={pop}
      actions={[
        <MainButton
          disabled={isCreating || isUpdating}
          onClick={() => form.handleSubmit(onSubmit)()}
          key={'add-update-btn'}
        >
          {isEdit ? t('update', { ns: 'common' }) : t('add', { ns: 'common' })}
        </MainButton>,
        <MainButton key="cancel" onClick={pop} variant="outlined">
          {t('cancel', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form}>
        <FormSelectField
          name={'impregnationType'}
          label={t('impregnation', { ns: 'impregnation' })}
          data={Object.keys(ImpregnationType).filter(key => !isNaN(Number(key)))}
          getOptionLabel={option => {
            return option != null && option != undefined && ImpregnationType[option as keyof typeof ImpregnationType]
              ? t(ImpregnationType[option as keyof typeof ImpregnationType].toString(), { ns: 'impregnation' })
              : ''
          }}
          onChange={(e, value) => {
            form.setValue('impregnationType', parseInt(value as any))
            form.clearErrors('impregnationType')
          }}
          renderOption={(props: any, option) => {
            return (
              <Box {...props} key={option}>
                {t(ImpregnationType[option as keyof typeof ImpregnationType].toString(), { ns: 'impregnation' })}
              </Box>
            )
          }}
        />
        <BasicDatePicker
          name="impregnationDate"
          label={t('impregnationDate', { ns: 'impregnation' })}
          onChange={date => {
            form.setValue('impregnationDate', getUTCDate(new Date(date)) as Date)
            setDate(date == undefined ? undefined : (getUTCDate(new Date(date)) as Date))
          }}
          value={date}
          shouldDisableDate={date => date > new Date()}
        />
        <FormTextField
          type="number"
          name="evaluation"
          label={t('evaluation', { ns: 'impregnation' })}
          autoFocus={false}
        />
        <FormTextField
          type="number"
          name="consumption"
          label={t('consumption', { ns: 'impregnation' })}
          autoFocus={false}
          defaultValue={0}
        />
        <FormTextField name="comment" label={t('comment', { ns: 'common' })} multiline rows={4} />
      </AppForm>
    </GenericDialogLayout>
  )
}

export default ImpregnationFormDialog
